import * as React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';
import { League, LeaguePhase, Team, LeagueGame } from '../../../sdk/model';
import { Box, Typography, Select, MenuItem, CircularProgress, Alert, Paper, useTheme, useMediaQuery } from '@mui/material';
import AllTeamsSchedule from './AllTeamsSchedule';
import TeamSchedule from '../team/TeamSchedule';
import TeamDisplay from '../team/TeamDisplay';
import PlayoffBracket from './PlayoffBracket';

interface GamesByWeek {
  [weekNumber: string]: LeagueGame[];
}

interface DisplayTeamProps {
  teamId: string;
  leagueId: string;
  team: Team;
  isUserTeam: boolean;
}

const PHASES_FOR_PLAYOFF_VIEW = new Set([
  LeaguePhase.Playoffs,
  LeaguePhase.SeasonComplete
]);

const DisplayTeam: React.FC<DisplayTeamProps> = ({ team, leagueId, isUserTeam }) => (
  <TeamDisplay 
    leagueId={leagueId} 
    team={team} 
    emphasized={isUserTeam} 
    teamId={undefined}
  />
);

function LeagueSchedule() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("teamId");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [viewingTeam, setViewingTeam] = React.useState<string>(teamId || 'all');
  const [scheduleType, setScheduleType] = React.useState<string>('all');
  const [isException, setIsException] = React.useState<boolean>(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);

  const user = useAppSelector((state) => state.user);
  
  const league = useLoadLeagueData({
    leagueId,
    userId: user.id,
    loadBasicDraftData: false,
    loadFullDraftData: false,
    loadContractData: false,
    loadBasicAuctionData: false,
    loadFullAuctionData: false,
    loadRosterData: false,
    loadRosterFor: [],  // Add this empty array since you're not loading roster data
    loadWaivedContracts: false,  // Add this with appropriate value
    loadScheduleData: true,
    isException,
    setIsException,
    isLoadComplete,
    setLoadComplete
  });

  const teamMap = React.useMemo(
    () => new Map(league?.teams?.map(team => [team.id, team]) || []),
    [league?.teams]
  );

  const gamesByWeek: GamesByWeek = React.useMemo(
    () => (league?.currentSeason?.schedule || []).reduce<GamesByWeek>((acc, game) => {
      if (!game.weekNumber) return acc;
      const week = game.weekNumber.toString();
      if (!acc[week]) {
        acc[week] = [];
      }
      acc[week].push(game);
      return acc;
    }, {}),
    [league?.currentSeason?.schedule]
  );

  // Set default view to playoffs if in playoff phase
  React.useEffect(() => {
    if (league?.phase && PHASES_FOR_PLAYOFF_VIEW.has(league.phase)) {
      setScheduleType('playoffs');
    }
  }, [league?.phase]);

  // Handle loading and error states
  
  if (!isLoadComplete) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isException || !league?.currentSeason?.schedule) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load schedule data. Please try again later.
      </Alert>
    );
  }

  const handleTeamChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setViewingTeam(event.target.value as string);
  };

  const handleScheduleTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setScheduleType(event.target.value as string);
  };

  const displayTeam = (teamId: string) => {
    const team = teamMap.get(teamId);
    if (!team) return null;
    
    return (
      <DisplayTeam
        teamId={teamId}
        leagueId={leagueId!}
        team={team}
        isUserTeam={team.owners?.some(owner => owner.id === user.id) ?? false}
      />
    );
  };

  return (
    <Box p={isMobile ? 1 : 3}>
      <Typography variant="h5" component="h1" mb={2} textAlign="center">
        League Schedule
      </Typography>
      
      <Box display="flex" justifyContent="center" mb={3}>
        <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
          <Select
            name="viewingTeam"
            value={viewingTeam}
            onChange={handleTeamChange}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Teams</MenuItem>
            {league.teams.map(team => (
              <MenuItem key={team.id} value={team.id}>
                {team.name}
              </MenuItem>
            ))}
          </Select>

          {viewingTeam === 'all' && (
            <Select
              name="scheduleType"
              value={scheduleType}
              onChange={handleScheduleTypeChange}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Games</MenuItem>
              <MenuItem value="playoffs">Playoffs Only</MenuItem>
            </Select>
          )}
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        {viewingTeam === 'all' ? (
          scheduleType === 'all' ? (
            <AllTeamsSchedule
              leagueId={leagueId!}
              gamesByWeek={gamesByWeek}
              displayTeam={displayTeam}
              teams={league.teams}
              editMode={false}
              editSchedule={null}
              canEditScores={false}
            />
          ) : (
            <PlayoffBracket
              leagueId={leagueId!}
              gamesByWeek={gamesByWeek}
              displayTeam={displayTeam}
              teams={league.teams}
              finalWeekOfRegularSeason={league.currentSeason.finalWeekOfRegularSeason!}
              playoffRoundConfig={league.currentSeason.playoffRoundConfig!}
            />
          )
        ) : (
          <TeamSchedule
            leagueId={leagueId!}
            gamesByWeek={gamesByWeek}
            displayTeam={displayTeam}
            teamId={viewingTeam}
          />
        )}
      </Paper>
    </Box>
  );
}

export default LeagueSchedule;
